/**
 * Ten God (十星) Based Compatibility — English output
 */
import type { SajuResult } from './sajuCalculations';
import { STEM_ELEMENTS, BRANCH_ELEMENTS } from './sajuCalculations';
import type { SipseongGoonghapResult } from './sipseongGoonghap';

const SIPSEONG_NAMES_EN = [
  'Companion','Rob Wealth','Eating God','Hurting Officer',
  'Indirect Wealth','Direct Wealth','7 Killings','Direct Officer',
  'Indirect Resource','Direct Resource',
];

function calculateSipseongEn(dayStemIdx: number, targetStemIdx: number): string {
  const diff = (targetStemIdx - dayStemIdx + 10) % 10;
  return SIPSEONG_NAMES_EN[diff];
}

const SIPSEONG_TRAITS_EN: Record<string, {
  nature: string;
  strength: string;
  weakness: string;
  loveStyle: string;
}> = {
  'Companion': {
    nature: 'Independent and self-reliant',
    strength: 'Strong personal values, works well alone',
    weakness: 'Stubborn, struggles to consider others\' opinions',
    loveStyle: 'Prefers freedom in love, dislikes being controlled',
  },
  'Rob Wealth': {
    nature: 'Competitive and action-driven',
    strength: 'Decisive action, relentless drive, entrepreneurial spirit',
    weakness: 'Impulsive, prone to competitive exhaustion',
    loveStyle: 'Passionate but prone to jealousy',
  },
  'Eating God': {
    nature: 'Relaxed, generous and optimistic',
    strength: 'Creativity, expressiveness, natural warmth',
    weakness: 'Laziness, overly optimistic, lack of discipline',
    loveStyle: 'Romantic and emotional, loves dates and gestures',
  },
  'Hurting Officer': {
    nature: 'Talented and self-expressive',
    strength: 'Skill, charisma, unique individuality',
    weakness: 'Self-centered, rebellious, unpredictable',
    loveStyle: 'Craves excitement, cannot stand boredom',
  },
  'Indirect Wealth': {
    nature: 'Opportunity-seeker with an adventurous spirit',
    strength: 'Revenue generation, business instinct, networking',
    weakness: 'Speculative, lacks stability, driven by desire',
    loveStyle: 'Master of push-and-pull, prefers stimulating relationships',
  },
  'Direct Wealth': {
    nature: 'Stable, planned and realistic',
    strength: 'Saving, management, consistency',
    weakness: 'Inflexible, can be overly frugal',
    loveStyle: 'Prefers stable, caring relationships',
  },
  '7 Killings': {
    nature: 'Charismatic with natural leadership',
    strength: 'Drive, decisiveness, strong sense of responsibility',
    weakness: 'Domineering, creates pressure, lacks flexibility',
    loveStyle: 'Leads in the relationship, strong protective instinct',
  },
  'Direct Officer': {
    nature: 'Principled, responsible and trustworthy',
    strength: 'Diligence, recognized competence, reliability',
    weakness: 'Conservative, inflexible, stress-prone',
    loveStyle: 'Takes relationships seriously, thinks about marriage early',
  },
  'Indirect Resource': {
    nature: 'Unique intuition and insight',
    strength: 'Creativity, analytical depth, expertise',
    weakness: 'Introverted, anxious, overthinks',
    loveStyle: 'Values deep connection, prefers a few intense bonds',
  },
  'Direct Resource': {
    nature: 'Caring and wise',
    strength: 'Learning ability, wisdom, supported by others',
    weakness: 'Dependent, prone to avoidance, unrealistic at times',
    loveStyle: 'Values warmth and emotional exchange',
  },
};

const SIPSEONG_COMPAT_EN: Record<string, Record<string, number>> = {
  'Companion':        { 'Companion':85,'Rob Wealth':80,'Eating God':80,'Hurting Officer':75,'Indirect Wealth':65,'Direct Wealth':70,'7 Killings':60,'Direct Officer':65,'Indirect Resource':85,'Direct Resource':80 },
  'Rob Wealth':       { 'Companion':80,'Rob Wealth':65,'Eating God':75,'Hurting Officer':80,'Indirect Wealth':70,'Direct Wealth':65,'7 Killings':75,'Direct Officer':70,'Indirect Resource':75,'Direct Resource':70 },
  'Eating God':       { 'Companion':80,'Rob Wealth':75,'Eating God':70,'Hurting Officer':75,'Indirect Wealth':85,'Direct Wealth':80,'7 Killings':60,'Direct Officer':65,'Indirect Resource':70,'Direct Resource':75 },
  'Hurting Officer':  { 'Companion':75,'Rob Wealth':80,'Eating God':75,'Hurting Officer':65,'Indirect Wealth':80,'Direct Wealth':75,'7 Killings':65,'Direct Officer':60,'Indirect Resource':75,'Direct Resource':70 },
  'Indirect Wealth':  { 'Companion':65,'Rob Wealth':70,'Eating God':85,'Hurting Officer':80,'Indirect Wealth':70,'Direct Wealth':75,'7 Killings':85,'Direct Officer':80,'Indirect Resource':65,'Direct Resource':70 },
  'Direct Wealth':    { 'Companion':70,'Rob Wealth':65,'Eating God':80,'Hurting Officer':75,'Indirect Wealth':75,'Direct Wealth':75,'7 Killings':80,'Direct Officer':85,'Indirect Resource':70,'Direct Resource':75 },
  '7 Killings':       { 'Companion':60,'Rob Wealth':75,'Eating God':60,'Hurting Officer':65,'Indirect Wealth':85,'Direct Wealth':80,'7 Killings':65,'Direct Officer':75,'Indirect Resource':70,'Direct Resource':75 },
  'Direct Officer':   { 'Companion':65,'Rob Wealth':70,'Eating God':65,'Hurting Officer':60,'Indirect Wealth':80,'Direct Wealth':85,'7 Killings':75,'Direct Officer':70,'Indirect Resource':75,'Direct Resource':80 },
  'Indirect Resource':{ 'Companion':85,'Rob Wealth':75,'Eating God':70,'Hurting Officer':75,'Indirect Wealth':65,'Direct Wealth':70,'7 Killings':70,'Direct Officer':75,'Indirect Resource':70,'Direct Resource':85 },
  'Direct Resource':  { 'Companion':80,'Rob Wealth':70,'Eating God':75,'Hurting Officer':70,'Indirect Wealth':70,'Direct Wealth':75,'7 Killings':75,'Direct Officer':80,'Indirect Resource':85,'Direct Resource':75 },
};

export function analyzeSipseongGoonghapEn(
  sajuA: SajuResult,
  sajuB: SajuResult,
  nameA: string,
  nameB: string
): SipseongGoonghapResult {
  const dayA = sajuA.dayPillar;
  const dayB = sajuB.dayPillar;

  const sipseongA = calculateSipseongEn(dayA.stemIdx, dayB.stemIdx);
  const sipseongB = calculateSipseongEn(dayB.stemIdx, dayA.stemIdx);

  const score = SIPSEONG_COMPAT_EN[sipseongA]?.[sipseongB] ?? 70;

  let level: SipseongGoonghapResult['compatibility']['level'];
  if (score >= 80) level = 'excellent';
  else if (score >= 70) level = 'good';
  else if (score >= 60) level = 'normal';
  else level = 'challenging';

  const a = nameA || 'Person A';
  const b = nameB || 'Person B';

  const summaries: Record<string, string> = {
    excellent: `${a} and ${b} are a perfect Ten God match. You naturally fill each other's gaps.`,
    good: `${a} and ${b} are a fairly compatible Ten God pair. Understanding and care will take you even further.`,
    normal: `${a} and ${b} have average Ten God compatibility. With sincere effort, this can become a wonderful relationship.`,
    challenging: `${a} and ${b} face a challenging Ten God pairing. Recognizing and respecting your differences is key.`,
  };

  const traitsA = SIPSEONG_TRAITS_EN[sipseongA] ?? SIPSEONG_TRAITS_EN['Companion'];
  const traitsB = SIPSEONG_TRAITS_EN[sipseongB] ?? SIPSEONG_TRAITS_EN['Companion'];

  const details: string[] = [
    `${a} is naturally ${traitsA.nature}.`,
    `${b} is naturally ${traitsB.nature}.`,
    sipseongA === sipseongB
      ? 'Your love styles are similar — you\'ll understand each other intuitively.'
      : `${a}'s love style: ${traitsA.loveStyle}`,
    sipseongA !== sipseongB ? `${b}'s love style: ${traitsB.loveStyle}` : '',
  ].filter(Boolean);

  const attraction = Math.min(100, Math.round((score + (sipseongA === 'Hurting Officer' || sipseongB === 'Hurting Officer' ? 10 : 0)) * 0.9));
  const stability  = Math.min(100, Math.round((score + (sipseongA === 'Direct Wealth' || sipseongB === 'Direct Wealth' ? 10 : 0)) * 0.85));
  const growth     = Math.min(100, Math.round((score + (sipseongA === 'Direct Resource' || sipseongB === 'Direct Resource' ? 8 : 0)) * 0.88));

  const chemistryDesc =
    score >= 80 ? "Your connection feels special from the start. The more time you spend together, the more positively you influence each other."
    : score >= 70 ? "You find each other genuinely attractive and have real potential to grow together. Communication is the deciding factor."
    : score >= 60 ? "You may feel different at first, but the more you discover about each other, the more the appeal grows."
    : "Your personalities differ, but those differences create the conditions for genuine mutual growth.";

  const advice = {
    strongPoint: score >= 75
      ? 'You naturally cover each other\'s weaknesses — a real synergy.'
      : score >= 65
        ? 'Different perspectives allow you to approach problems in complementary ways.'
        : 'The differences between you create rich opportunities for learning.',
    watchOut: score < 65
      ? "Ignoring each other's different opinions will build resentment over time."
      : "Don't take familiarity for granted — gratitude keeps the spark alive.",
    tip: score >= 80
      ? "This compatibility is rare. Treasure what you have."
      : score >= 70
        ? "Schedule regular check-ins to share how you're truly feeling."
        : "See differences as 'different,' not 'wrong.' That shift changes everything.",
  };

  return {
    personA: {
      name: a,
      daySipseong: sipseongA,
      traits: {
        nature: traitsA.nature,
        strength: traitsA.strength,
        weakness: traitsA.weakness,
        loveStyle: traitsA.loveStyle,
        careerFit: [],
        moneyStyle: '',
      },
      elements: {
        stem: STEM_ELEMENTS[dayA.stemIdx],
        branch: BRANCH_ELEMENTS[dayA.branchIdx],
      },
    },
    personB: {
      name: b,
      daySipseong: sipseongB,
      traits: {
        nature: traitsB.nature,
        strength: traitsB.strength,
        weakness: traitsB.weakness,
        loveStyle: traitsB.loveStyle,
        careerFit: [],
        moneyStyle: '',
      },
      elements: {
        stem: STEM_ELEMENTS[dayB.stemIdx],
        branch: BRANCH_ELEMENTS[dayB.branchIdx],
      },
    },
    compatibility: { score, level, summary: summaries[level], details },
    chemistry: { attraction, stability, growth, description: chemistryDesc },
    advice,
  };
}